import {
  AmbientLight,
  Box3,
  Color,
  DirectionalLight,
  Group,
  PCFSoftShadowMap,
  Raycaster,
  Scene,
  Vector2,
  WebGLRenderer,
} from 'three'
import { Vector3 } from 'three'
import { BoxSizeEnum, SoundTypeEnum, type SplineType } from './core/enums'
import { CONFETTI_SOUND, SUCCESS_PANEL_DELAY, SUCCESS_PANEL_SOUND } from './game/soundLibrary'
import { convertPosition } from './core/unity'
import { LEVEL_COUNT, loadLevel, requestedLevelFrom, resolvedFileIndex, type Level } from './core/levelData'
import { DEFAULT_SPLINE_TUNING, type SplineTuning } from './core/spline/splineGenerator'
import { CRASH_HOLD_SECONDS, GameSession, type SessionOutcome } from './game/session'
import { chooseDispatch } from './game/planner'
import {
  BUS_CAPACITY,
  busMiddleSpan,
  busCabinExtension,
  DEFAULT_BUS_BODY_TUNING,
  cloneGameplayTuning,
  type BusBodyTuning,
  type GameplayTuning,
  ICE_BREAK_VFX,
} from './game/tuning'
import { AssetLibrary } from './render/assetLibrary'
import { BusFleet } from './render/busView'
import { CharacterView } from './render/characterView'
import { EnvironmentView, environmentMeshes } from './render/environmentView'
import { GameCamera } from './render/gameCamera'
import { BUS_SINGLE_MESH, SessionView } from './render/sessionView'
import { SoundPlayer } from './render/soundPlayer'
import { BootLoader } from './ui/bootLoader'
import { LocalSave } from './ui/localSave'
import { AdService } from './monetization/ads'
import { iapService } from './monetization/iap'
import { bootBeacons } from './monetization/lifecycle'
import { cloudSave } from './monetization/cloudSave'
import { nativeHost } from './monetization/host'
import { LevelTurn, readLevelStats, saveLevelStats } from './monetization/levelLog'
import { createSun, fitSunShadow } from './render/sunShadow'
import { SPLINE_DEBUG_LEGEND, SplineDebug } from './render/splineDebug'
import { GameUi } from './ui/gameUi'
import { mountNineSlices, watchPixelRatio } from './ui/nineSlice'
import { bannerSeconds, FAIL_TEXT } from './ui/animatedText'
import { tutorialTarget } from './ui/tutorialCanvas'

interface Teardown {
  dispose(): void
}

/**
 * How high above the sign's origin `ConveyorLimitText` sits.
 *
 * 📐, and no longer a guess: the label is a child of `ConveyorIndicator` in
 * `Gameplay.unity`, and both of its transforms survived. The label's
 * `m_AnchoredPosition` is `(0, 1.844)` in the indicator's local space
 * (`:3809`), and the indicator's own `m_LocalScale` is `1.601` (`:1722`), so
 * the world offset is `1.844 * 1.601`. The old 3.1 was eyeballed off the mesh's
 * height and is 0.15 too high.
 */
const SIGN_LABEL_HEIGHT = 1.844 * 1.601

/**
 * 🔍 GC-2 — `Time.timeScale` while the last buses finish their lap.
 *
 * `GameplayController.OnAllBusesDispatched:24` publishes
 * `TimeScaleChangedEvent(0x3fe00000)`, and `OnTimeScaleChanged:15` does exactly
 * one thing with it: `Time.timeScale = e.TimeScale`. The field is a `float`, so
 * those bits are **1.75** — see the note in `session.ts` for why 0.5 is the
 * wrong reading. Every other publisher in the build sends 1.0, and the two that
 * send 0 (`FailPresenter.Initialize`, `SettingsPresenter.Initialize`) freeze the
 * game behind a popup the port does not have yet.
 *
 * This lives in the view because `timeScale` multiplies `deltaTime` uniformly:
 * every rule advances at the same relative pace and no ordering changes. The
 * acceptance test for that claim is `npm run solve`, which drives `GameSession`
 * directly and never reaches this file — if it moves, this is in the wrong
 * layer.
 */
const TIME_SCALE_ALL_DISPATCHED = 1.75

/** Scratch for the tutorial hand target, which is read once a frame. */
const _tutorialTarget = new Vector3()


/** Tuning entries a slider can drive — everything but `waitingAreaBounds`. */
type NumericTuningKey = {
  [K in keyof GameplayTuning]: GameplayTuning[K] extends number ? K : never
}[keyof GameplayTuning]

/** `localStorage` throws in some sandboxes; a save that cannot persist still runs. */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

class App {
  private renderer: WebGLRenderer
  private scene = new Scene()
  private gameCamera = new GameCamera()
  private levelRoot = new Group()
  private sun: DirectionalLight
  /**
   * The level's lowest and highest surface, measured off the built scene, so
   * the shadow map covers the slab the camera actually sees. Refitted on every
   * level and every resize.
   */
  private shadowBand = { min: -1, max: 1 }
  private teardowns: Teardown[] = []
  private raycaster = new Raycaster()
  private pointer = new Vector2()

  private splineTuning: SplineTuning = { ...DEFAULT_SPLINE_TUNING }
  private gameplayTuning: GameplayTuning = cloneGameplayTuning()
  /**
   * Shared with the live `BusFleet`, not copied into it, so dragging a slider
   * changes the next frame. Survives a level reload — the point is to hold a
   * setting still while comparing against the reference clip.
   */
  private busBodyTuning: BusBodyTuning = { ...DEFAULT_BUS_BODY_TUNING }

  /**
   * ⚠️ **Scaffolding, and now off by default.**
   *
   * It existed because a finished level had nothing to move it on: the original
   * ends one with a panel — `HandleLevelComplete` loads `SuccessPresenter`, and
   * the fail path reaches `FailPresenter` through `TryShowFailPopup` — and the
   * port had neither. Both are ported now, so the flow is the player's again:
   * **Continue** on a win, **Try Again** on a loss.
   *
   * Kept behind `?autoflow=1` for sweeps that want to walk the level list
   * without clicking.
   */
  autoFlow = false
  /** Cancels a queued advance when the level changes under it. */
  private flowToken = 0
  /**
   * The wait `<OnLevelFailed>g__TryShowFailPopup` stands in for — it opens the
   * panel only once **both** the banner and the crash animation are done, and
   * the crash's 1.3 s is the longer of the two. Cleared when the level changes.
   */
  private endPanel = 0

  private session: GameSession | null = null
  /**
   * IC-7b. Starts disabled and is switched on by the first click — a browser
   * refuses playback before a gesture, so arming it there costs nothing and
   * keeps a freshly loaded page silent.
   */
  private readonly sound = new SoundPlayer()
  /** `SaveService.Data` — the two switches the settings panel flips. */
  private readonly save = new LocalSave(safeLocalStorage())
  /**
   * ❓ Not in the game — `log_level`, one event per play turn. See
   * `monetization/levelLog.ts`; the counters are local and have their own key.
   */
  private readonly levelTurn = new LevelTurn(
    readLevelStats(safeLocalStorage()),
    (stats) => saveLevelStats(safeLocalStorage(), stats),
  )
  /**
   * Passengers on the board when the current level finished building. The turn
   * needs it twice — to size the level (difficulty) and to score it
   * (`levelProgress`) — and it must be the built board's count, not the file's.
   */
  private levelPassengers = 0
  /**
   * ❓ Not in the game: interstitials. See `web/docs/ADS-MONETIZATION.md`.
   * Held here rather than in `GameUi` because the ad sits between a popup
   * button and a level load, and only this class knows about level loads.
   */
  private readonly ads = new AdService()
  private view: SessionView | null = null
  /**
   * The static dressing. Built once and kept across level loads — the ground,
   * the platform and the road stubs are scene objects, not level data.
   */
  private environment: EnvironmentView | null = null
  private splineDebug: SplineDebug | null = null
  private currentLevel = 1
  /** The spline overlay is a tool, not part of the game. Off unless asked for. */
  private showSplineDebug = false
  private lastTime = performance.now()
  /** `Time.timeScale`. `GameplayController.OnLevelLoaded` publishes 1.0. */
  private timeScale = 1
  private status = ''
  /** The game's own HUD — the restart button, the level label, the counter. */
  private readonly ui = new GameUi({
    /*
     * `GamePlayUIModel.OnRestartButtonClicked` — 🔍 the three steps, in order:
     *
     *   _soundService.Play(SoundTypeEnum.ButtonClick);   // = 2
     *   EventBus.Publish(new LevelFailedEvent(...));
     *   _popupService.Load<SceneLoadPresenter>(request);
     *
     * The published `LevelFailedEvent` carries `FailReason.Restart` (3), and
     * `GameplayController.OnLevelFailed` (`__0x211d880.c`:83) returns on exactly
     * that value **before** the fail sound, the crash animation and the banner.
     * So a restart is a loss on the books and a silent one on screen — which is
     * why reloading straight away is the faithful thing to do here, and why no
     * `onOutcome` is faked.
     *
     * The `SceneLoadPresenter` wipe wraps this in `GameUi` (`sceneWipe.ts`).
     */
    onRestart: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.reload()
    },
    /*
     * `GamePlayUIModel.OnSettingsButtonClicked` — 🔍 `__0x21462b0.c`: load the
     * settings popup, then `SoundService.ExecutePlay(1, 1, 0, svc, 2, 0)`.
     *
     * 🔍 `GamePlayUIModel.OnSettingsButtonClicked` — the click, then
     * `PopupService.Load<SettingsPresenter>`; and `SettingsPresenter.Initialize`
     * opens with `TimeScaleChangedEvent(0f)`, so the board freezes under it.
     * The tuning panel this button used to open is on the `h` key.
     */
    onSettings: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.openSettings()
    },
    /*
     * ❓ Not in the game — the Remove Ads panel. Borrows the settings button's
     * shape (click sound, then open) because that is the only precedent the
     * HUD has for opening a panel. Unlike settings it does **not** stop the
     * clock: the panel can only be reached between levels anyway, and freezing
     * would be a rule change made by a store screen.
     */
    onNoAds: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.ui.showNoAds(this.save.data.displayLevel)
    },
    /** ❓ Not in the game — the panel's cross. Just the click, like every other close. */
    onCloseNoAds: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
    },
    /*
     * 🔍 `SettingsModel.OnSoundButtonClicked` — `SoundService.ToggleSound`,
     * `Taptic.Medium` (no haptics here), then the click through
     * `ExecutePlay`, the internal entry that skips the `IsSoundEnabled` gate —
     * so switching sound *off* still clicks.
     */
    onToggleSound: () => {
      const on = this.save.toggleSound()
      this.sound.enabled = on
      this.sound.playUnconditionally(SoundTypeEnum.ButtonClick)
      return on
    },
    /** 🔍 `SettingsModel.OnHapticButtonClicked` — the same, for a switch the port cannot honour. */
    onToggleHaptic: () => {
      const on = this.save.toggleHaptic()
      this.sound.playUnconditionally(SoundTypeEnum.ButtonClick)
      return on
    },
    /** 🔍 `SettingsModel.OnCloseButtonClicked` — click, `TimeScaleChangedEvent(1f)`, unload. */
    onCloseSettings: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.timeScale = 1
    },
    /*
     * `FailModel.TryAgainButtonClick` — 🔍 `__0x213a52c.c`:
     * `SoundService.Play(ButtonClick)`, then the scene-load popup and the level
     * again. Same shape as the HUD's restart, so it takes the same iris.
     */
    onTryAgain: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.ui.closePopups()
      void this.withInterstitial(() => this.ui.showWipe(() => this.reload()))
    },
    /*
     * 🔍 `FailPresenter.OnCloseButtonClicked` — `__0x213af10.c`, size 0x18: the
     * same six instructions as `OnTryAgainButtonClicked` (`__0x213af28.c`),
     * both flowing into `FailModel.TryAgainButtonClick`. `FailModel.CloseButtonClick`
     * (0x213a640, size 4) is the `ret` at `TryAgainButtonClick + 0x114` and
     * nothing jumps to it. So the cross is a second Try Again — there is no
     * "close and look at the lost board"; that is what Hold To See does.
     *
     * Which is why the interstitial slot is here too, and why the body has to
     * stay byte-identical to `onTryAgain`: `popupLayout.test.ts` compares the
     * two sources, and an ad the player can skip with an X is not a slot.
     */
    onCloseFail: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.ui.closePopups()
      void this.withInterstitial(() => this.ui.showWipe(() => this.reload()))
    },
    /*
     * The win panel's Continue — sound, then the level the ladder is already
     * standing on. 🔍 `SuccessModel.LoadLNextLevel` (0x2141cdc) is
     * `LevelService.LoadLevel` inlined: it **reads** `_userLevel` at
     * `lVar5+0x10` and never writes it. The step was taken back in
     * `OnLevelCompleted`, the moment the win was announced, so Continue only
     * resolves it to a file. Stepping here as well would skip a level.
     */
    onContinue: () => {
      this.sound.play(SoundTypeEnum.ButtonClick)
      this.ui.closePopups()
      void this.withInterstitial(() =>
        this.ui.showWipe(() => void this.load(resolvedFileIndex(this.save.data.displayLevel))),
      )
    },
  })

  constructor(
    private canvas: HTMLCanvasElement,
    private stage: HTMLElement,
  ) {
    this.renderer = new WebGLRenderer({ canvas, antialias: true })
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2))
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = PCFSoftShadowMap

    // `Gameplay.unity` clears to the default skybox, but the `Ground` plate is
    // 108x162 and covers every pixel the camera can see, so what actually shows
    // behind the level is the plate's own colour. Matching it here keeps the
    // seam invisible on a screen wider than the plate.
    this.scene.background = new Color().setRGB(0.78, 0.792, 0.969, 'srgb')
    this.scene.add(this.levelRoot)
    // Only the placeholder boxes are lit by these; everything drawn from the
    // shipped art goes through `ToonMaterial`, which has its own single sun.
    this.scene.add(new AmbientLight(0xffffff, 1.7))

    // The scene's `Directional Light`, casting — see `sunShadow.ts` for what is
    // read off it and what is not. The target has to be in the graph for its
    // world matrix to follow `fitSunShadow`'s moves.
    this.sun = createSun()
    this.scene.add(this.sun)
    this.scene.add(this.sun.target)

    // ❓ Not in the game — fan the store's answer out to the three places that
    // need it, then ask the app once at boot. Apply it immediately too: the
    // flag is restored from localStorage in `IapService`'s constructor, so a
    // payer must not see one ad before the app replies.
    iapService.onAdsRemovedChanged((removed) => this.applyAdsRemoved(removed))
    this.applyAdsRemoved(iapService.adsRemoved)
    void iapService.refreshEntitlement()

    // ❓ Not in the game — the save's second destination. Hung on `onSaved`
    // rather than added to each of `toggleSound` / `toggleHaptic` /
    // `completeLevel` / `skipToLevel`, so a call site added later cannot forget
    // it. `SaveService.SaveLocalAsync` is still the first destination and the
    // authoritative one; this is a mirror.
    this.save.onSaved = (data) => cloudSave.push(data)

    // WebBridge.md §1 — the notch inset, published as a CSS variable so the
    // stylesheet can use it. ⬜ Nothing consumes `--sbh` yet: the HUD is laid
    // out in prefab units (`var(--u)`) that `popupLayout.test.ts` locks, and
    // moving it is `rule_HEADER`'s job, not the bridge's. Exposed rather than
    // applied, so the value is live and testable when that port happens.
    document.documentElement.style.setProperty('--sbh', `${nativeHost.statusBarHeight}px`)

    addEventListener('resize', () => this.resize())
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e))
    this.resize()
  }

  private resize(): void {
    // The stage, not the window: on anything wider than a phone the stage is a
    // portrait strip in the middle of the page, and handing the camera the
    // window's aspect would frame the level for a shape the art was never
    // authored for.
    const width = Math.max(1, this.stage.clientWidth)
    const height = Math.max(1, this.stage.clientHeight)
    this.renderer.setSize(width, height, false)
    this.gameCamera.resize(width, height)
    this.fitShadow()
  }

  /** Fit the sun's shadow box to the visible slab — after any camera or level change. */
  private fitShadow(): void {
    fitSunShadow(this.sun, this.gameCamera.camera, this.shadowBand.min, this.shadowBand.max)
  }

  private onPointerDown(event: PointerEvent): void {
    // A gesture is exactly what a browser wants before it will play anything,
    // so the first click is where audio comes on (IC-7b) — to whatever the
    // save says (`SoundService.InitializeSettings`).
    this.sound.enabled = this.save.data.isSoundEnabled
    if (!this.session || !this.view) return
    const rect = this.canvas.getBoundingClientRect()
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    // Not `setFromCamera` on its own: the camera's near plane is negative, and
    // `GameCamera.aimRaycaster` is where that is carried into the ray's own
    // distance window. Without it the bottom row of the lot is unclickable.
    this.gameCamera.aimRaycaster(this.raycaster, this.pointer)

    const hits = this.raycaster.intersectObjects(this.view.pickTargets, false)
    if (hits.length === 0) return
    const bus = this.view.busFor(hits[0].object)
    if (!bus) return

    const result = this.session.click(bus)
    if (result === 'nudged') this.status = `bus ${bus.id} is blocked`
    else if (result === 'dispatched') this.status = `bus ${bus.id} heading for the road`
    else this.status = 'road is at capacity'
    this.refreshHud()
  }

  /**
   * The shipped art, or null if it could not be fetched.
   *
   * Since AST-4 `public/assets/` is committed, so a clone has the art without
   * running anything — null here means a fetch failed, not "not built yet".
   * The placeholder-box fallback stays anyway: it is what keeps a broken or
   * mis-rooted server (`CLAUDE.md` §8, Vite answering 200 with `index.html`)
   * from being an unhandled rejection instead of a visibly wrong scene.
   */
  private assets: AssetLibrary | null = null

  private async loadAssets(): Promise<void> {
    if (this.assets) return
    try {
      const library = await AssetLibrary.load()
      this.boot.set('manifest')
      await library.loadMeshes([
        ...BusFleet.requiredMeshes(library),
        // `Stone_1..4`, the ice shards. Nothing references them through a prefab —
        // they hang off `ice_vfx`'s ParticleSystemRenderer — so they are asked for by
        // name, the same way `buildAssets` collects them. See `ICE_BREAK_VFX`.
        ...ICE_BREAK_VFX.meshes,
        // And the emitter surface itself — the one-piece body under `Ice/Fake`,
        // which is never drawn and so never collected. See `BUS_SINGLE_MESH`.
        ...Object.values(BUS_SINGLE_MESH),
        ...CharacterView.requiredMeshes(library),
        ...environmentMeshes(library),
      ], (done, total) => this.boot.set('meshes', done, total))
      // Before the sheet lifts, not after: `texture()` hands materials an
      // empty `Texture` and fills it in later, so a scene shown the moment the
      // meshes land draws with no albedo and no `uChannelMap*` — the level in
      // pale grey until the images catch up. See `AssetLibrary.loadTextures`.
      await library.loadTextures((done, total) => this.boot.set('textures', done, total))
      this.assets = library
    } catch (error) {
      console.warn('[assets] not loaded, falling back to placeholder shapes:', error)
    }
  }

  async load(levelNumber: number): Promise<void> {
    // Invalidates any queued auto-flow, including the one that may have called
    // us: whatever it wanted to do next is now the current level's business.
    this.flowToken++
    // 🔍 A turn left open here is an abandoned one — a restart, or Try Again.
    // `GameplayController.OnLevelFailed` (`__0x211d880.c`:83) returns on
    // `FailReason.Restart` before anything is announced, so the original logs
    // no result either. rule_LOGLEVEL §6 files that as deliberate.
    this.levelTurn.abandon()
    try {
      await this.loadAssets()
      this.boot.set('scene', 0, 1)
      const fetched = performance.now()
      const level = await loadLevel(levelNumber)
      const parsed = performance.now()
      this.currentLevel = levelNumber
      await this.build(level)
      this.boot.set('scene')
      this.loadCost = { level: parsed - fetched, build: performance.now() - parsed }
      this.syncLocationLevel(levelNumber)
      this.renderHud()
    } finally {
      // `SceneLoadPresenter.Close()` — the iris opens once the new scene is
      // standing, and "standing" means *after* the rebuild, not during it.
      // Opening it from `setLevel` used to start the reveal while `build` was
      // still allocating geometry, so the first frames of the wipe fought the
      // rebuild for the main thread and the transition visibly stuttered.
      //
      // In `finally` because the iris covers the screen: a load that throws
      // must not leave the player looking at a closed one for ever.
      requestAnimationFrame(() => {
        this.ui.showBoard()
        this.dropBootCover()
        // WebBridge.md §3 — the board is on screen and a tap would do
        // something. Latched inside `BootBeacons`, so the restart and Continue
        // paths that also come through here send nothing. In the `finally`
        // deliberately: a load that threw still leaves the player looking at
        // the page, and an app that never hears `game_ready` holds its splash
        // over it for ever.
        bootBeacons.gameReady()
      })
    }
  }

  /**
   * `Bootstrapper.unity` — the solid frame the first load happens under.
   *
   * 🔍 `Bootstrapper.<Awake>d__10.MoveNext` (`__0x20d51d8.c`): after
   * `InitializeServices` it calls `LoadInitialScene` (`__0x20d4a5c.c`), which
   * publishes `SceneChangeRequestedEvent(gameConfig.gameplaySceneIndex)` to
   * `SceneLoaderService.OnSceneChangeRequested` → `LoadSceneAsync`, whose
   * state machine (`__0x20daa58.c`) runs `SceneManager.LoadSceneAsync(index,
   * Single)`. `PopupService` is not on that path, so no `SceneLoadPresenter`
   * exists on the first load and nothing irises open: the Bootstrapper scene —
   * a SolidColor camera and an empty Canvas — is replaced by the gameplay
   * scene in one frame. `#bootCover` is that camera frame, shipped in
   * `index.html` so it is painted before the module loads; it goes the frame
   * the first level is standing, and every later load finds it gone. The
   * restart / continue paths keep the iris, which the original loads only from
   * `GamePlayUIModel.OnRestartButtonClicked`, `FailModel` and `SuccessModel`.
   */
  private bootCover: HTMLElement | null = document.getElementById('bootCover')

  /**
   * The bar on that sheet. ⚠️ Not in the original — the original's first load
   * waits on nothing. See `bootLoader.ts` for why the web build's does.
   */
  private readonly boot = new BootLoader(this.bootCover)

  private dropBootCover(): void {
    this.bootCover?.remove()
    this.bootCover = null
  }

  /** What the last level change cost, in ms. Shown in the dev panel. */
  private loadCost = { level: 0, build: 0 }

  /**
   * Keep `?level=` pointing at what is on screen.
   *
   * `replaceState`, not `pushState`: the level is a place you are, not a step
   * in a history you want to walk back through — with auto-flow on, `pushState`
   * would bury the back button under one entry per level cleared. The point is
   * that a refresh, or a copied URL, lands where you were.
   */
  private syncLocationLevel(levelNumber: number): void {
    const url = new URL(location.href)
    if (url.searchParams.get('level') === String(levelNumber)) return
    url.searchParams.set('level', String(levelNumber))
    history.replaceState(null, '', url)
  }

  /** ❓ Not in the game — the entitlement's three destinations. */
  private applyAdsRemoved(removed: boolean): void {
    this.ads.adsRemoved = removed
    this.ui.setAdsRemoved(removed)
  }

  reload(): void {
    void this.load(this.currentLevel)
  }

  /**
   * The interstitial slot the three end-of-level buttons share: show an ad if
   * the policy allows one, *then* do what the button was going to do.
   *
   * ❓ Not in the game — see `web/docs/ADS-MONETIZATION.md` §B.2. Two decisions
   * worth stating, because both are easy to get backwards:
   *
   * 1. The ad runs **before** `showWipe`, not inside its callback. The iris is
   *    the port's scene-load curtain; an ad opened behind it would land on the
   *    player *after* the next level is already standing.
   * 2. The level handed to the gate is `displayLevel`, the ladder step, not
   *    `currentLevel`, the file index. The file index wraps back to 16 past the
   *    last level (`GetResolvedFileIndex`), so a min-level gate written against
   *    it would re-close for a player who has passed 40. The ladder only climbs.
   *
   * `maybeInterstitial` never rejects, so `then` always runs.
   */
  private async withInterstitial(then: () => void): Promise<void> {
    await this.ads.maybeInterstitial(this.save.data.displayLevel)
    then()
  }

  /**
   * 🔍 `LevelService..ctor` (`__0x20d5ccc.c`:30) — `_userLevel` is read out of
   * `SaveService.Data.displayLevel`, then resolved to a file. The file number
   * to open when nothing asked for a particular one.
   */
  resumeLevel(): number {
    return resolvedFileIndex(this.save.data.displayLevel)
  }

  /**
   * WebBridge.md §3 `request_user_data` — fold the app's copy of the save into
   * this device's, **before** anything reads `displayLevel`.
   *
   * Order is the whole point and it is easy to lose: `resumeLevel` and
   * `skipToLevel` both read the ladder, so a hydrate that happened after them
   * would restore the player's progress into a variable nobody looks at again
   * and open on level 1 anyway. It is awaited at boot for the same reason —
   * `CloudSave` has its own timeout, so this can wait without being able to
   * hang. See `monetization/cloudSave.ts` for the merge and its bound.
   */
  async hydrateFromCloud(): Promise<void> {
    await cloudSave.hydrate(this.save)
  }

  /** 🔍 `LevelService.SkipToLevel` (0x20d838c) — set the ladder, then save. */
  skipToLevel(levelNumber: number): number {
    return resolvedFileIndex(this.save.skipToLevel(levelNumber))
  }

  /**
   * Stand in for the win/fail panels: advance on a win, retry on a loss.
   *
   * The two delays are not the same kind of number, and the difference is worth
   * keeping straight:
   *
   *  - **Loss — `CRASH_HOLD_SECONDS` (1.3 s), 🔍.** `PlayFailCrashAnimation`
   *    ends in `DOVirtual.DelayedCall(1.3f, onComplete)`, and that `onComplete`
   *    is `<OnLevelFailed>g__TryShowFailPopup`. So 1.3 s is exactly when the
   *    original puts something on screen; retrying then is the original's own
   *    timing, not a taste call. It also lets the crash roll finish, which is
   *    the part of the fail path the port *does* have.
   *  - **Win — ❓ PLACEHOLDER, dev-only.** `OnAllQueuesCleared`:53 loads the
   *    success popup with **no delay at all**. A hold here is purely so a human
   *    can see the level was won before the screen changes; it is scaffolding,
   *    and it must not be mistaken for a recovered constant. Nothing in the
   *    original corresponds to it.
   */
  private static readonly AUTO_FLOW_WIN_HOLD_SECONDS = 1

  private scheduleAutoFlow(outcome: SessionOutcome): void {
    if (!this.autoFlow || outcome === 'playing') return
    const token = ++this.flowToken
    const won = outcome === 'complete'
    const delay = won ? App.AUTO_FLOW_WIN_HOLD_SECONDS : CRASH_HOLD_SECONDS
    setTimeout(() => {
      // A manual level change, a reload, or a second outcome bumps the token;
      // the stale timer then finds itself out of date and does nothing. Without
      // this a queued advance fires on top of whatever the player moved to.
      if (token !== this.flowToken) return
      if (won) {
        // The same door the panel's Continue uses: the ladder already stepped.
        void this.load(resolvedFileIndex(this.save.data.displayLevel))
      } else {
        this.reload()
      }
    }, delay * 1000)
  }

  /**
   * Run the simulation forward without drawing.
   *
   * At t = 0 the waiting areas are empty and the queues are still stacked at
   * their spawn points, so a screenshot of a freshly loaded level shows a state
   * no player ever sees. Headless Chrome's virtual clock does not drive
   * `requestAnimationFrame`, so it cannot be asked to wait either — hence
   * `?seek=8`, which steps the session at a fixed 60 Hz first.
   */
  seek(seconds: number): void {
    const session = this.session
    if (!session) return
    let sinceClick = 0
    for (let f = 0; f < Math.round(seconds * 60); f++) {
      // Same cadence `npm run solve` uses, so a seek with autoplay on lands in
      // the same states the headless run measures.
      if (this.autoplay) {
        sinceClick += 1 / 60
        if (sinceClick >= 0.25) {
          sinceClick = 0
          const pick = chooseDispatch(session)
          if (pick) session.click(pick)
        }
      }
      session.update(1 / 60)
      // …and the turn clock, for the same reason as the view below, one layer
      // further out. `log_level.duration` is fed by `App.start`'s tick, which
      // is not running yet when `seek` is called, so a seek that skipped this
      // line would report `duration: 0` on a level it had just played through
      // forty seconds of — and the next person to check the analytics in a
      // screenshot run would find a zero and go looking for a bug in the clock.
      // `true` because a seeked frame *is* a played frame: the round is live by
      // construction, and once it ends `LevelTurn.end` has closed the turn and
      // this is a no-op.
      this.levelTurn.tick(1 / 60, true)
      // 🔴 **The view is stepped too, every frame — this used to be one
      // `sync(0)` after the loop, and that made every seek screenshot a lie.**
      //
      // The rules are not the only thing on a clock. `BusFleet.advance` runs
      // the accommodation wave off `rideTime`, `ConveyorIndicatorView` runs the
      // alarm off `alarmPhase`, the skid ribbons age, the particle pools age.
      // All of them start at zero and are advanced by `sync`, so seeking to
      // t = 20 and syncing once with `dt = 0` put every one of them at the
      // *beginning* of its animation: buses twenty seconds into a lap were
      // drawn with the whole ribbed wave still unfolded across them, which is
      // what a bus looks like in its first tenth of a second on the road.
      //
      // That cost real time. Chasing it produced a "the port draws ribs the
      // original does not" finding, complete with A/B pixel counts, before the
      // rings turned out to be an artefact of this line. It is the same trap as
      // IC-10 — build or advance the view *after* winding the clock and it
      // reports the state it was born in, not the state you asked for — one
      // layer up, in the browser rather than in `renderPreview`.
      this.view?.sync(1 / 60)
      // And the environment's half of the same frame, exactly as `start`'s
      // tick does it (ELV-10). The elevator lids sat outside `view.sync` and
      // were only ever written by the live loop, so a `?seek=` past a layer's
      // rise drew its lids at rest size on a platform already 8 u up — the
      // §8 "clock of the drawing layer" trap again, one call narrower.
      this.environment?.syncElevators(session)
      this.environment?.syncDoors(session.areas)
    }
  }

  /**
   * Let the planner drive, so a screenshot can catch a bus mid-road.
   *
   * Nothing moves in this game without a tap, so `?seek` on its own only ever
   * shows the opening position with the queues drained a little. Anything that
   * only happens to a bus already on the loop — the body bending, the tunnel
   * mouths swallowing it — is invisible without this.
   */
  autoplay = false

  private async build(level: Level): Promise<void> {
    for (const t of this.teardowns) t.dispose()
    this.teardowns = []
    this.levelRoot.clear()
    this.status = ''
    // `GameplayController.OnLevelLoaded` and `.OnRestartRequested` both publish
    // `TimeScaleChangedEvent(1.0)`; this one call covers both, since a restart
    // rebuilds the level here.
    this.timeScale = 1

    const session = new GameSession(level, {
      splineTuning: this.splineTuning,
      gameplayTuning: this.gameplayTuning,
      events: {
        // IC-7b. Silent until a click switches it on — browsers refuse audio
        // before a gesture anyway, and a page that starts making noise on load
        // is worse than one that starts quiet.
        onSound: this.sound.play,
        // `GamePlayUIPresenter.OnFirstInputDetected` — the restart button comes
        // back on with the player's first tap on a bus.
        onFirstInput: () => {
          this.ui.firstInput()
          // `TutorialController.OnBusClicked` — HideTutorial, then Destroy the
          // canvas. `BusClickedEvent` comes from `Bus.OnBoxClicked`, which is a
          // frame ahead of `FirstInputDetectedEvent` but only ever matters for
          // the first tap, and that is the same tap.
          this.ui.hideTutorial()
        },
        onOutcome: (outcome, reason) => {
          this.status = `${outcome.toUpperCase()} — ${reason}`
          // 🔍 `OnLevelFailed` kills both buttons; `OnLevelSucceeded` has the
          // same body but nothing ever publishes `LevelSucceededEvent`, so a win
          // leaves the HUD exactly as it was.
          // `log_level` — `Fail` on the lose event itself, not on the panel.
          // rule_LOGLEVEL §4c warns that the two differ when a revive exists;
          // 🔍 this game has none — `FailPresenter.OnCloseButtonClicked`
          // (`__0x213af10.c`) is the same six instructions as Try Again, so both
          // reload — which makes this the point of no return. `end` is idempotent
          // regardless, so the panel's two exits cannot double the fail rate.
          if (outcome === 'failed') {
            this.levelTurn.end('Fail', this.passengersLeft)
            this.ui.levelFailed()
            // 🔍 `GameplayController.OnLevelFailed` switches `AnimatedText` on
            // and plays it, then `TryShowFailPopup` opens `FailPresenter` once
            // **both** the banner and the crash animation are done — the panel
            // waits for whichever finishes last, which is the crash's own 1.3 s
            // `DOVirtual.DelayedCall`.
            this.ui.showFailBanner()
            const wait = Math.max(CRASH_HOLD_SECONDS, bannerSeconds(FAIL_TEXT.outOfMoves.length))
            this.endPanel = window.setTimeout(() => this.ui.showFailPopup(level.number), wait * 1000)
          } else if (outcome === 'complete') {
            // `log_level` — `Pass`, and **before** `completeLevel`: the event
            // reports the step that was just beaten, not the one the ladder is
            // about to move to. (`LevelTurn` snapshots the step at `begin`, so
            // the order is belt-and-braces rather than the only guard — but the
            // two lines being adjacent is exactly where a later edit would swap
            // them.) `levelProgress` is 100 by definition, so `passengersLeft`
            // is not consulted: the last passengers may still be walking.
            this.levelTurn.end('Pass', 0)
            // 🔍 `LevelService.OnLevelCompleted` (0x20d7f70) — the ladder steps
            // once, here, on the event that announces the win, and is saved on
            // the spot. `GameSession.finish` returns early once the outcome has
            // left `'playing'`, so this runs exactly once per level.
            this.save.completeLevel()
            // 🔍 `HandleLevelComplete` loads the popup with no delay at all.
            this.ui.showSuccessPopup(level.number)
            // 🔍 `SuccessAnimations.Show`:77 — `Play(Confetti)` inside the
            // confetti loop, so it lands with the panel. Once, not once per
            // particle system: the same-sound cooldown collapses the loop's
            // repeats into one clip anyway, and the array's length is a prefab
            // detail the export dropped.
            this.sound.play(CONFETTI_SOUND)
            // 🔍 …and `Build(SuccessPanel).WithDelay(1.4f).Play()` at :104-110,
            // the only `SoundBuilder` the gameplay flow reaches. The delay is
            // the builder's, not a tween's, so it survives the panel's own
            // animation finishing early.
            this.endPanel = window.setTimeout(
              () => this.sound.play(SUCCESS_PANEL_SOUND),
              SUCCESS_PANEL_DELAY * 1000,
            )
          }
          // `GameplayController.OnAllQueuesCleared` and `.OnLevelFailed` both
          // publish `TimeScaleChangedEvent(1.0)` — the sprint below ends the
          // moment the level does, either way.
          this.timeScale = 1
          this.renderHud()
          this.scheduleAutoFlow(outcome)
        },
        // GC-2. `GameplayController.OnAllBusesDispatched` → 1.75×. The same
        // event reaches `GamePlayUIPresenter.OnAllBusesDispatched`, whose whole
        // body is `SetRestartButtonInteractable(false)`.
        onAllBusesDispatched: () => {
          this.timeScale = TIME_SCALE_ALL_DISPATCHED
          this.ui.allBusesDispatched()
        },
        // 🔍 `BusCrashVFXManager.OnBusCrashed` — the manager's only job is to
        // move its one pooled instance to the hit and play it. The view owns
        // the pool, so the subscription is a one-liner here, exactly as the
        // original's subscription is a one-liner in `OnEnable`.
        onBusCrashed: (position) => this.view?.fireCrashBurst(position),
        // 🔍 `OnBoxClicked`'s inlined `AnimateCantMoveIndicator` — the label
        // over a bus the road has no room for, by count or by length.
        onCantMove: (bus, spaceMaxed) => this.view?.showCantMove(bus.position, spaceMaxed),
        // 🔍 `Bus.DeactivateHiddenState` → `SetFeature(Empty)` → `ExitHiddenState`:
        // the material tween is the view's (HID-5); the rule and the sound
        // stay in the session, which is what raises this.
        onBusRevealed: (bus) => this.view?.busRevealed(bus),
      },
    })
    this.session = session

    // The ground, the road and the stops. Built from the shipped art; the
    // static half survives the level change, so only the road and the stations
    // are rebuilt here.
    if (this.assets) {
      if (!this.environment) {
        this.environment = new EnvironmentView(this.assets)
        this.scene.add(this.environment.object)
      }
      this.environment.build(level, session.road)
    }

    // ⚠️ Built **only when it is on**. The overlay is a tool and it is off by
    // default, but it used to be constructed on every level change — geometry
    // for the whole spline, thrown away unseen, right in the middle of the one
    // moment the port drops frames. `buildSplineDebug` is what the toggle calls.
    this.splineDebug = null
    if (this.showSplineDebug) this.buildSplineDebug()

    const view = new SessionView(session, this.assets, this.busBodyTuning)
    this.levelRoot.add(view.object)
    this.teardowns.push(view)
    this.view = view

    this.ui.setLevel(level.number)
    this.ui.setBanner('')
    // `GameplayController.OnLevelLoaded` — `textAnimator.gameObject.SetActive(false)`.
    this.ui.clearFailBanner()
    // And `PopupService.Unload` takes the end-of-level panel with the level.
    window.clearTimeout(this.endPanel)
    this.ui.closePopups()
    this.ui.setCapacityAnchor(
      level.conveyorIndicatorPosition
        ? convertPosition(level.conveyorIndicatorPosition, new Vector3()).setY(SIGN_LABEL_HEIGHT)
        : null,
    )

    // 🔍 `TutorialController.OnLevelLoaded` -> `CheckAndShowTutorial`: the hand
    // only ever appears on level 1 (`_levelService._userLevel != 1` -> return),
    // pointing at the first box in `BusController._boxes`. It is torn down again
    // by the first `BusClickedEvent`, which the port raises as `onFirstInput`.
    //
    // 🔍 The index is **2**, the third box: `MoveNext`:177 reads
    // `List<>.get_Item(_boxes, 2)`. The port used to take box 0 because the
    // guard right above is only `Count > 0`, which does not make `[2]` safe —
    // but a mismatched guard is a bug in the original, not licence to pick a
    // different box. Level 1 has three boxes, so the read is in range there,
    // and level 1 is the only level the hand runs on.
    const target = session.buses[2]
    if (level.number === 1 && target) {
      // ⚠️ **No `convertPosition` here.** `Bus.position` is already in the
      // port's space: `Bus`'s constructor converts the level's Unity vector
      // once (`bus.ts`:308). Converting again flipped z, which mirrored the
      // target across the car park — on level 1 the hand aimed at (270, 701)
      // in a 545x941 stage while box 0 is drawn in (198..336, 485..557), so it
      // sat on bare asphalt below the whole rank. Same trap as
      // `SessionView`:749.
      this.ui.showTutorial(() => tutorialTarget(target, _tutorialTarget))
    } else {
      this.ui.hideTutorial()
    }

    this.gameCamera.applyLevel(level.camera)
    this.resize()
    view.sync()

    // 📐 The slab the shadow map has to cover: from the lowest surface in the
    // level (an elevator's lower layer sits at y −8) to the highest, read off
    // what was just built rather than assumed. Only the heights are used —
    // the plate is 108×162 and mostly off-screen, so its x/z would waste the
    // map; `fitSunShadow` takes the footprint from the camera instead.
    const bounds = new Box3().setFromObject(this.levelRoot)
    if (this.environment) bounds.expandByObject(this.environment.object)
    if (!bounds.isEmpty()) this.shadowBand = { min: bounds.min.y, max: bounds.max.y }
    this.fitShadow()

    // WebBridge.md §3 `log_level` — `Start`, at the end of the build rather
    // than at the click that asked for the level: rule_LOGLEVEL §4a is explicit
    // that the size must be counted off the board that actually exists.
    //
    // `displayLevel` is the ladder step and `level.number` the file. Past step
    // 40 🔍 `GetResolvedFileIndex` wraps back to file 16, so the two part
    // company for good and both are reported — see `LevelLog.currentLevelId`.
    this.levelPassengers = level.queues.reduce((n, queue) => n + queue.people.length, 0)
    this.levelTurn.begin({
      ladderStep: this.save.data.displayLevel,
      fileIndex: level.number,
      passengers: this.levelPassengers,
    })
  }

  /**
   * Passengers still on the platform: waiting in a queue, or standing in a
   * waiting area. Someone already walking to a bus is in neither, and counts as
   * cleared — rule_LOGLEVEL §4b's "mid-animation counts", and true here for a
   * stronger reason than convention: no play of the board can put them back.
   */
  private get passengersLeft(): number {
    const stats = this.session?.stats
    return stats ? stats.waiting + stats.inAreas : 0
  }

  start(): void {
    const tick = (now: number) => {
      // The clamp is load-bearing for `log_level.duration` as well as for the
      // simulation: a WebView halts `requestAnimationFrame` while backgrounded,
      // and without the cap the single resume frame would hand the whole
      // away-gap to the turn clock. rule_LOGLEVEL §4d.
      const realDt = Math.min((now - this.lastTime) / 1000, 1 / 20)
      const dt = realDt * this.timeScale
      this.lastTime = now

      // `log_level.duration` — **real** seconds, not simulated ones, so the
      // 1.75× sprint after the last dispatch does not inflate them; and only
      // while the round is genuinely live and on screen. The four guards, in
      // order: a turn is open, the settings pause is not up (`timeScale` is 0
      // while the panel is), the round has not ended, and the tab is visible
      // *and* the app is foregrounded (`nativeHost.isActive` folds both).
      // Wall-clock here would bill an AFK phone an eight-hour level.
      this.levelTurn.tick(
        realDt,
        this.timeScale > 0 && this.session?.outcome === 'playing' && nativeHost.isActive,
      )

      if (this.session && this.view) {
        this.session.update(dt)
        this.view.sync(dt)
        this.environment?.syncElevators(this.session)
        this.splineDebug?.sync(this.session)
        this.environment?.syncDoors(this.session.areas)
        // 🔍 `SplineController.RefreshConveyorIndicator` computes the sign's
        // number as `_activeSplineBuses.Count + MovingBusesCount +
        // _activeBusRequests.Count + _busRequestQueue.Count`, which is exactly
        // `session.conveyorIndicator`. The old source here — `stats.onRoad`, i.e.
        // `followers.size` — counted buses already peeling off onto the exit
        // curve and missed every bus still driving out of the lot, so the sign
        // read a number the original never shows.
        const sign = this.session.conveyorIndicator
        this.ui.setCapacity(sign.current, sign.max, sign.spaceMaxed)
        // The sign's own half: the two red lamps, the pulse when the count goes
        // up, and the kerb turning red while the road is full.
        this.environment?.syncIndicator(this.session, dt)
        this.ui.sync(this.gameCamera.camera, dt)
      }
      this.renderer.render(this.scene, this.gameCamera.camera)
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
    setInterval(() => this.refreshHud(), 250)
  }

  /**
   * Show or hide the tuning rig.
   *
   * It exists to set the `[SerializeField]`s IL2CPP dropped against the
   * reference clip, and it is not part of the game — the reference screenshot
   * has a restart button, a level label and a gear, and nothing else. Omit the
   * argument to flip it.
   */
  /** `PopupService.Load<SettingsPresenter>` — and the pause `Initialize` brings. */
  openSettings(): void {
    this.ui.showSettings({ sound: this.save.data.isSoundEnabled, haptic: this.save.data.isHapticEnabled })
    this.timeScale = 0
  }

  /**
   * Open one of the other three panels on demand — for screenshots only.
   *
   * The settings panel has had `?settings=1` since it was ported, and the win,
   * fail and Remove Ads panels have had nothing, so the only way to a picture of
   * them was to play a level out. That left all three carrying "verified by
   * reasoning, not by a picture" in the checklist for days, which is exactly the
   * state a 9-slice seam hides in. This is the door.
   *
   * It shows the panel and nothing else: no loss is recorded, no level is
   * completed, no ad is asked for. `PopupService` opens these the same way from
   * any caller, so what appears here is what appears in a real game.
   */
  openPopup(kind: 'fail' | 'success' | 'noads'): void {
    if (kind === 'fail') this.ui.showFailPopup(this.currentLevel)
    else if (kind === 'success') this.ui.showSuccessPopup(this.currentLevel)
    else this.ui.showNoAds(this.currentLevel)
    this.timeScale = 0
  }

  setDevPanel(show?: boolean): void {
    const hud = document.getElementById('hud')
    if (!hud) return
    hud.hidden = show === undefined ? !hud.hidden : !show
    if (!hud.hidden) this.renderHud()
  }

  toggleSplineDebug(): void {
    this.showSplineDebug = !this.showSplineDebug
    if (this.showSplineDebug && !this.splineDebug) this.buildSplineDebug()
    if (this.splineDebug) this.splineDebug.object.visible = this.showSplineDebug
  }

  /** The overlay's geometry, built the first time it is actually asked for. */
  private buildSplineDebug(): void {
    if (!this.session) return
    const debug = new SplineDebug(this.session.road, this.session)
    debug.object.visible = this.showSplineDebug
    this.levelRoot.add(debug.object)
    this.teardowns.push(debug)
    this.splineDebug = debug
  }

  // -------------------------------------------------------------------------

  /**
   * The knobs that are **still unknown**. A slider here is a question the port
   * has not answered; once a value is read out of the original, its slider
   * goes.
   *
   * Sixteen of these have gone that way, all recovered from `.ctor` field
   * initialisers (`WaitingArea$.ctor__0x2136444.c`, `QueueController$.ctor`) or
   * from a tooltip:
   *
   *     skin 0.02 · flow 6 · flow Z 0 · smooth 0.12 · passes 3 · tilt 4 ·
   *     max tilt 30 · stiffness 200 · damping 5 · whiplash 15 · wobble s 1.2 ·
   *     facing 180 · spread 15 · walk in 0.3 · board walk 16.8 · hop 4
   *
   * They are not knobs any more, so exposing them invites someone to drag the
   * port away from a value that was measured. The three left are the three that
   * are genuinely still open — see each `title`.
   */
  private readonly peopleSliders: {
    id: string
    key: NumericTuningKey
    label: string
    title: string
    min: number
    max: number
    step: number
  }[] = [
    { id: 'pScale', key: 'queueCharacterScale', label: 'scale', title: 'PeopleQueue.characterScale — 📐 1.13, pinned between the original frame (queue rows 1.4 apart show no belt between them, so >= 1.129) and the bus roof (0.9 + 0.625s <= 1.649, so <= 1.198). The .ctor says 1.55 and the screen falsifies it. Settle it properly by measuring a body against the platform width in reference/gameplay.mp4.', min: 0.4, max: 2, step: 0.01 },
    { id: 'pRadius', key: 'waitingAreaCharacterRadius', label: 'radius', title: 'WaitingArea.characterRadius — 📐 0.69, from a frame of the original: the luminance period along a queue run and across the waiting-area crowd are both 7.0 px, and the queue period is the known 1.4, so 2r + skin = 1.4. The .ctor says 0.42 and is stale. Settle it properly by counting heads on the plate in reference/gameplay.mp4.', min: 0.2, max: 1.4, step: 0.01 }
  ]

  /**
   * `Bus.passengerHeadExtension` / `passengerTailExtension`, one pair per prefab.
   *
   * These set how far riders reach past each joint, out over the caps, on top
   * of the middle body (`busMiddleSpan`, a share of the road). They also widen
   * the road a bus claims in `IsSpaceClearForBus` and the gap
   * `ComputeTrafficSpeedFactor` demands, so moving them changes traffic as much
   * as looks. `busCabinExtension` is where the cabin floor runs out; past that
   * riders are standing on the bodywork.
   */
  private readonly extensionSliders: {
    id: string
    field: 'passengerHeadExtension' | 'passengerTailExtension'
    size: BoxSizeEnum
    label: string
  }[] = [
    { id: 'xHeadS', field: 'passengerHeadExtension', size: BoxSizeEnum.Small, label: 'S head' },
    { id: 'xTailS', field: 'passengerTailExtension', size: BoxSizeEnum.Small, label: 'S tail' },
    { id: 'xHeadM', field: 'passengerHeadExtension', size: BoxSizeEnum.Medium, label: 'M head' },
    { id: 'xTailM', field: 'passengerTailExtension', size: BoxSizeEnum.Medium, label: 'M tail' },
    { id: 'xHeadL', field: 'passengerHeadExtension', size: BoxSizeEnum.Large, label: 'L head' },
    { id: 'xTailL', field: 'passengerTailExtension', size: BoxSizeEnum.Large, label: 'L tail' },
  ]

  /**
   * Row spacing each bus size ends up with, as the sliders move.
   *
   * Depends on the level: the middle body is a share of the road, so the same
   * bus packs looser on a longer loop.
   */
  private riderSpacingLabel(): string {
    const loop = this.session?.road.spline.length ?? 0
    return [BoxSizeEnum.Small, BoxSizeEnum.Medium, BoxSizeEnum.Large]
      .map((size) => {
        const rows = Math.max(1, Math.trunc(Math.max(2, BUS_CAPACITY[size]) / 3))
        const spacing =
          (this.gameplayTuning.passengerHeadExtension[size] +
            this.gameplayTuning.passengerTailExtension[size] +
            busMiddleSpan(size, loop)) /
          rows
        return spacing.toFixed(2)
      })
      .join(' / ')
  }

  /**
   * Which `<details>` the reader has opened.
   *
   * Kept on the app, not in the DOM, because the panel is rebuilt wholesale and
   * anything living only in the markup dies with it. Without this a group
   * opened by a click closed again on the next refresh — which read as "the
   * group will not open at all", since the refresh is 250 ms away.
   */
  private readonly openGroups = new Set<string>()

  /** Just the live counters, so a refresh need not rebuild the controls. */
  private statsHtml(): string {
    const session = this.session
    if (!session) return ''
    const s = session.stats
    return `
        <dt>parked</dt><dd>${s.parked}</dd>
        <dt>on road</dt><dd>${s.onRoad} / ${this.gameplayTuning.maxBusesOnSpline}</dd>
        <dt>held at stop</dt><dd>${s.held}</dd>
        <dt>departed</dt><dd>${s.gone} / ${session.buses.length}</dd>
        <dt>in waiting area</dt><dd>${s.inAreas} / ${s.areaCapacity}</dd>
        <dt>in queue</dt><dd>${s.waiting}</dd>
        <dt>gpu geo/tex</dt><dd>${this.gpuCounts().geometries} / ${this.gpuCounts().textures}</dd>`
  }

  /**
   * What the GPU is still holding.
   *
   * `WebGLRenderer.info.memory` counts live WebGL objects, not JS ones, so it
   * is the honest answer to "did the last level actually let go": a geometry
   * that is unreachable from the scene graph but never `dispose()`d stays in
   * here until the context dies. A number that climbs level after level is a
   * leak; one that returns to where it started is not.
   *
   * Public so `?cycle=` can sample it from outside.
   */
  gpuCounts(): { geometries: number; textures: number } {
    return {
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
    }
  }

  /**
   * Load every level in turn, `rounds` times, sampling what the GPU holds.
   *
   * A diagnostic, not a test: `npm test` has no WebGL context, so the only
   * place this measurement can be taken is a real browser. Reached with
   * `?cycle=2`, and it writes its verdict into the dev HUD's status line so a
   * screenshot is enough to read it.
   */
  async cycleLevels(rounds: number): Promise<string> {
    // A first pass warms everything a level can allocate; comparing against
    // the count *before* that would report the warm-up as a leak.
    for (let n = 1; n <= LEVEL_COUNT; n++) await this.load(n)
    const before = this.gpuCounts()
    const samples: string[] = []
    for (let round = 0; round < rounds; round++) {
      for (let n = 1; n <= LEVEL_COUNT; n++) await this.load(n)
      const now = this.gpuCounts()
      samples.push(`${now.geometries}/${now.textures}`)
    }
    const after = this.gpuCounts()
    const verdict =
      after.geometries === before.geometries && after.textures === before.textures
        ? 'STABLE'
        : `LEAK +${after.geometries - before.geometries}geo +${after.textures - before.textures}tex`
    const line = `cycle ${rounds}x${LEVEL_COUNT}: ${before.geometries}/${before.textures} -> ${samples.join(' ')} ${verdict}`
    this.status = line
    this.renderHud()
    console.log('[cycle]', line)
    return line
  }

  /**
   * The 4 Hz update.
   *
   * Touches **only** the numbers. Rebuilding the whole panel on a timer is what
   * made the rig unusable: it closed every `<details>` and tore the slider out
   * from under a drag, four times a second.
   */
  refreshHud(): void {
    const hud = document.getElementById('hud')
    const session = this.session
    if (!hud || !session || hud.hidden) return

    const stats = document.getElementById('stats')
    if (!stats) {
      // Nothing has been built yet (first tick after the panel was shown).
      this.renderHud()
      return
    }
    stats.innerHTML = this.statsHtml()

    const outcomeClass: Record<SessionOutcome, string> = {
      playing: '',
      complete: 'ok',
      failed: 'bad',
    }
    const status = document.getElementById('status')
    if (status) {
      status.className = `status ${outcomeClass[session.outcome]}`
      // The load cost rides along because a level change is the one moment the
      // port drops frames, and "it feels laggy" is not something to fix blind.
      const cost = `${this.loadCost.level.toFixed(0)}+${this.loadCost.build.toFixed(0)} ms`
      status.textContent = `${this.status || 'click a bus'} · load ${cost}`
    }

    const spacing = document.getElementById('spacing')
    if (spacing) spacing.textContent = this.riderSpacingLabel()
  }

  renderHud(): void {
    const hud = document.getElementById('hud')
    const session = this.session
    if (!hud || !session || hud.hidden) return

    const outcomeClass: Record<SessionOutcome, string> = {
      playing: '',
      complete: 'ok',
      failed: 'bad',
    }

    hud.innerHTML = `
      <div class="row">
        <button id="prev">◀</button>
        <span class="level">Level ${session.level.number}</span>
        <button id="next">▶</button>
      </div>
      <dl id="stats">${this.statsHtml()}</dl>
      <div id="status" class="status ${outcomeClass[session.outcome]}">${
        this.status || 'click a bus'
      }</div>
      <div class="row">
        <label title="BusSplineFollower.passengerTopOffset — the compartment floor riders stand on. The art puts it at 2.76, rim at 4.01. Lost [SerializeField] — set by eye.">deck</label>
        <input id="deck" type="range" min="0.5" max="4.2" step="0.02"
               value="${this.gameplayTuning.passengerTopOffset}">
        <span>${this.gameplayTuning.passengerTopOffset.toFixed(2)}</span>
      </div>
      <div class="row">
        <label title="Bus.PlayAccommodationAnimation totalDuration — the staggered ring wave as the bus takes to the road. Lost [SerializeField] — set by eye.">ring wave</label>
        <input id="ringWave" type="range" min="0.2" max="3" step="0.05"
               value="${this.busBodyTuning.accommodationAnimationSeconds}">
        <span>${this.busBodyTuning.accommodationAnimationSeconds.toFixed(2)}s</span>
      </div>
      <details class="group" data-group="people" ${this.openGroups.has('people') ? 'open' : ''}>
        <summary>passengers — still unknown</summary>
        ${this.peopleSliders
          .map(
            (s) => `
        <div class="row">
          <label title="${s.title}">${s.label}</label>
          <input id="${s.id}" type="range" min="${s.min}" max="${s.max}" step="${s.step}"
                 value="${this.gameplayTuning[s.key]}">
          <span>${(this.gameplayTuning[s.key] as number).toFixed(2)}</span>
        </div>`,
          )
          .join('')}
      </details>
      <details class="group" data-group="packing" ${this.openGroups.has('packing') ? 'open' : ''}>
        <summary>rider packing — Bus.passenger*Extension</summary>
        ${this.extensionSliders
          .map((s) => {
            const value = this.gameplayTuning[s.field][s.size]
            const cabin = busCabinExtension(s.size)
            const limit = s.field === 'passengerHeadExtension' ? cabin.head : cabin.tail
            return `
        <div class="row">
          <label title="Bus.${s.field} on ConveyorBox (${s.label[0]}) — how far past that joint riders may stand, and how much road the bus claims. The flat roof runs out at ${limit.toFixed(2)}; past that they are on the bodywork.">${s.label}</label>
          <input id="${s.id}" type="range" min="0" max="${(limit * 1.4).toFixed(2)}" step="0.05" value="${value}">
          <span>${value.toFixed(2)}${value > limit + 1e-6 ? ' !' : ''}</span>
        </div>`
          })
          .join('')}
        <div class="row"><label>spacing</label><span id="spacing">${this.riderSpacingLabel()}</span></div>
      </details>
      <div class="row">
        <label>basis</label>
        <select id="basis">
          <option value="">BSpline (as shipped)</option>
          <option value="0">Catmull-Rom</option>
          <option value="3">Linear</option>
        </select>
      </div>
      <div class="row">
        <button id="restart">restart</button>
        <button id="toggleSpline">${this.showSplineDebug ? 'hide' : 'show'} splines</button>
      </div>
      ${
        this.showSplineDebug
          ? `<div class="legend">${SPLINE_DEBUG_LEGEND.map(
              (item) =>
                `<div><i style="background:#${item.colour
                  .toString(16)
                  .padStart(6, '0')}"></i>${item.label}</div>`,
            ).join('')}</div>`
          : ''
      }
    `

    const go = (n: number) => {
      void this.load(((n - 1 + LEVEL_COUNT) % LEVEL_COUNT) + 1)
    }
    document.getElementById('prev')?.addEventListener('click', () => go(session.level.number - 1))
    document.getElementById('next')?.addEventListener('click', () => go(session.level.number + 1))
    document.getElementById('restart')?.addEventListener('click', () => this.reload())
    document.getElementById('toggleSpline')?.addEventListener('click', () => {
      this.toggleSplineDebug()
      this.renderHud()
    })

    const deck = document.getElementById('deck') as HTMLInputElement | null
    deck?.addEventListener('input', () => {
      const value = Number(deck.value)
      this.gameplayTuning.passengerTopOffset = value
      session.tuning.passengerTopOffset = value
      const readout = deck.nextElementSibling
      if (readout) readout.textContent = value.toFixed(2)
    })

    const ringWave = document.getElementById('ringWave') as HTMLInputElement | null
    ringWave?.addEventListener('input', () => {
      const value = Number(ringWave.value)
      this.busBodyTuning.accommodationAnimationSeconds = value
      const readout = ringWave.nextElementSibling
      if (readout) readout.textContent = `${value.toFixed(2)}s`
    })

    // Remember which groups are open. Re-rendering replaces these elements, so
    // the flag has to live outside the DOM or every refresh closes them.
    for (const group of hud.querySelectorAll('details[data-group]')) {
      const name = (group as HTMLElement).dataset.group
      if (!name) continue
      group.addEventListener('toggle', () => {
        if ((group as HTMLDetailsElement).open) this.openGroups.add(name)
        else this.openGroups.delete(name)
      })
    }

    for (const slider of this.peopleSliders) {
      const input = document.getElementById(slider.id) as HTMLInputElement | null
      input?.addEventListener('input', () => {
        const value = Number(input.value)
        this.gameplayTuning[slider.key] = value
        session.tuning[slider.key] = value
        // Update the readout in place. Calling `renderHud()` here would replace
        // the very element being dragged, which ends the drag after one step.
        const readout = input.nextElementSibling
        if (readout) readout.textContent = value.toFixed(2)
      })
    }

    for (const slider of this.extensionSliders) {
      const input = document.getElementById(slider.id) as HTMLInputElement | null
      input?.addEventListener('input', () => {
        // `session.tuning` *is* `gameplayTuning`; the record is shared with it
        // and with nothing else, thanks to `cloneGameplayTuning`.
        const value = Number(input.value)
        this.gameplayTuning[slider.field][slider.size] = value
        const cabin = busCabinExtension(slider.size)
        const limit = slider.field === 'passengerHeadExtension' ? cabin.head : cabin.tail
        const readout = input.nextElementSibling
        if (readout) readout.textContent = `${value.toFixed(2)}${value > limit + 1e-6 ? ' !' : ''}`
        const spacing = document.getElementById('spacing')
        if (spacing) spacing.textContent = this.riderSpacingLabel()
      })
    }

    const basis = document.getElementById('basis') as HTMLSelectElement | null
    if (basis) {
      basis.value =
        this.splineTuning.basisOverride === null ? '' : String(this.splineTuning.basisOverride)
      basis.addEventListener('change', () => {
        this.splineTuning.basisOverride =
          basis.value === '' ? null : (Number(basis.value) as SplineType)
        this.reload()
      })
    }
  }
}

// WebBridge.md §3 — the bundle is executing. First line of real work on
// purpose: it is what lets the app stop watching for a white screen, and every
// line below it can throw.
bootBeacons.webLoaded()

const canvas = document.getElementById('game') as HTMLCanvasElement
const stage = document.getElementById('stage') as HTMLElement
const app = new App(canvas, stage)

const params = new URLSearchParams(location.search)
// `?level=` was clamped at the bottom only, so `?level=99` asked `loadLevel`
// for a file that is not there and the page came up on a rejected promise.
// Clamp both ends and round, so any number lands on a level that exists.
//
// ⚠️ The parse is in `requestedLevelFrom`, not inline here, and it branches on
// the raw string rather than the parsed number: `params.get` answers `null`
// when the parameter is absent, `Number(null)` is `0` — not `NaN` — and
// `Number.isFinite(0)` is `true`. Branching on the number therefore swallowed
// every bare URL and ran it through `skipToLevel`, which *writes*, so the save
// was not merely ignored on a visit to `/` — it was overwritten with level 1
// on every one of them, and WEB-4's ladder could never be seen in a browser.
const requested = requestedLevelFrom(params.get('level'))
// …but before either door onto the ladder is opened, take the app's copy of the
// save. Both `skipToLevel` and `resumeLevel` *read* `displayLevel`, and
// `skipToLevel` also writes it, so a hydrate that ran after this line would be
// restoring progress nobody is going to look at. Bounded by `CloudSave`'s own
// timeout, and a plain browser answers immediately.
await app.hydrateFromCloud()
// WEB-4. With no `?level=`, resume where the save left off — 🔍
// `LevelService..ctor` (`__0x20d5ccc.c`:30) seeds `_userLevel` from
// `SaveService.Data.displayLevel` and then plays `GetResolvedFileIndex` of it;
// a fresh save is step 1, so a first visit still opens on level 1.
//
// With `?level=N`, the port's own door onto a chosen level, that is written
// back through `LocalSave.skipToLevel` — the port decision here is that
// `?level=` *is* `SkipToLevel` (0x20d838c), which does save, rather than a
// second, competing notion of where the player is. `SkipToLevel`'s ladder step
// and this file number differ only past file 40, and both resolve to the same
// file from then on, so the round trip through `syncLocationLevel` is lossless
// in what the player sees.
const startLevel = requested !== null ? app.skipToLevel(requested) : app.resumeLevel()
// Temporary: the win/fail panels do not exist yet, so the flow runs itself.
if (params.get('autoflow') === '0') app.autoFlow = false
await app.load(startLevel)
if (params.get('autoplay') === '1') app.autoplay = true
const seek = Number(params.get('seek'))
if (Number.isFinite(seek) && seek > 0) app.seek(Math.min(seek, 600))
if (params.get('hud') === '1') app.setDevPanel(true)
// `?instant=1` — `BasePopupPresenter.Open(instant: true)`, the original's own
// switch for skipping a panel's tweens. Here it is for screenshots: the win
// panel's timeline runs 3.25 s and a headless virtual-time budget is spent by
// the time `seek` returns, so without it every capture shows a bare scrim.
if (params.get('instant') === '1') document.documentElement.classList.add('instant')
// `?settings=1` — open the settings panel on load, for screenshots.
if (params.get('settings') === '1') app.openSettings()
// `?popup=fail|success|noads` — the other three panels, same purpose. Pair it
// with `&instant=1` unless you want the panel mid-tween.
const popup = params.get('popup')
if (popup === 'fail' || popup === 'success' || popup === 'noads') app.openPopup(popup)

/*
 * Repaint every 9-sliced panel as one bitmap. Here, after the popups exist,
 * because it reads the `border-image` back off their own computed style — see
 * `nineSlice.ts` for why nine separate quads cannot be made to meet.
 */
/*
 * `?noslice=1` — leave the panels on `border-image` alone, so the repaint can
 * be A/B'd against the thing it replaces. Measured through it (settings frame,
 * DPR 1.25, page scale 0.80, with the CSS backing bars disabled too): repaint
 * on **-0.5**, repaint off **-43.7**. Without a switch like this the two are
 * indistinguishable, because the bars already hold the visible case — the same
 * `check:tunnel` trap as ever: a measurement needs something to measure against.
 */
if (params.get('noslice') !== '1') {
  mountNineSlices()
  watchPixelRatio()
}
app.start()

// `?cycle=N` — the resource-leak probe. See `App.cycleLevels`.
const cycle = Math.round(Number(params.get('cycle')))
if (Number.isFinite(cycle) && cycle > 0) {
  app.autoFlow = false
  void app.cycleLevels(Math.min(cycle, 5))
}

addEventListener('keydown', (e) => {
  if (e.key === 'r') app.reload()
  // The tuning rig, not part of the game — same panel the gear button opens.
  if (e.key === 'h') app.setDevPanel()
  if (e.key === 's') app.toggleSplineDebug()
})
